import { useEffect, useMemo, useState } from 'react';
import { storesApi } from '@/api/stores.api';
import type { SelectOption } from '@/components/ui/Select';
import type { StoreResponse } from '@/types/store';
import { formatStoreLabel, formatStoreShort } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function useStoreOptions(pageSize = 200) {
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    storesApi
      .list({ page: 0, size: pageSize, activeOnly: true })
      .then((res) => setStores(res.content))
      .catch((err) => showApiError(err, 'Failed to load stores'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () => stores.map((s) => ({ value: String(s.id), label: formatStoreLabel(s) })),
    [stores],
  );

  const byId = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);

  const getStore = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined, short = false) => {
    const row = getStore(id);
    if (!row) return id != null && id !== '' ? `#${id}` : '—';
    return short ? formatStoreShort(row) : formatStoreLabel(row);
  };

  return { stores, options, loading, getStore, getLabel, byId };
}
